import { motion } from "framer-motion";
import { Crown } from "lucide-react";
import { SCENES, ROYAL } from "@/lib/images";

const recognitions = [
  {
    title: "Edmonton City Council & Legislature of Alberta",
    year: "2019 · 100th Birthday",
    note: "On her centennial birthday, Agnes was honoured by the Edmonton City Council and the Legislative Assembly of Alberta — a city and province pausing to salute a century of life.",
    rotate: "-rotate-2",
    img: ROYAL.certificates100,
    imgAlt: "Edmonton City Council and Alberta Legislature certificates for Agnes Murphy's 100th birthday",
    size: "md:col-span-2",
  },
  {
    title: "Queen Elizabeth II",
    year: "2019 · 100th Birthday",
    note: "Her Majesty recognised Agnes's extraordinary century with a personal congratulatory message — a hundred years honoured by the Crown.",
    rotate: "rotate-1",
    img: ROYAL.queenElizabeth,
    imgAlt: "Royal letter from Queen Elizabeth II",
    size: "",
  },
  {
    title: "King Charles III",
    year: "2024 · 105th Birthday",
    note: "Five years on, at 105, a heartfelt message arrived from His Majesty King Charles III and Queen Camilla — a rare and treasured recognition.",
    rotate: "-rotate-1",
    img: ROYAL.kingCharles,
    imgAlt: "Royal letter from King Charles III and Queen Camilla",
    size: "",
  },
  {
    title: "Governor General of Canada — Mary Simon",
    year: "2024 · 105th Birthday",
    note: "\"Your experience is valuable and respected, and we can all learn from the wisdom you have to share.\" — Governor General Mary Simon, writing from Rideau Hall.",
    rotate: "rotate-2",
    img: "/images/agnes-governor-general-letter-clean.jpg",
    imgAlt: "Letter from Governor General Mary Simon to Agnes Murphy on her 105th birthday",
    imgContain: true,
    size: "",
  },
  {
    title: "Prime Minister of Canada",
    year: "2024 · 105th Birthday",
    note: "\"It is a great pleasure to send you best wishes and warmest congratulations on the occasion of your one hundred and fifth birthday.\" — Prime Minister of Canada, Ottawa 2024.",
    rotate: "-rotate-2",
    img: ROYAL.primeMinister,
    imgAlt: "Letter from the Prime Minister of Canada to Agnes Murphy on her 105th birthday",
    size: "",
  },
  {
    title: "Lieutenant Governor of Alberta — Salma Lakhani",
    year: "2026 · 107th Birthday",
    note: "Her Honour, the Honourable Salma Lakhani, AOE, BSc, LLD (hon), Lieutenant Governor of the Province of Alberta, offered her very best wishes for good health and happiness on Agnes's remarkable 107th birthday.",
    rotate: "rotate-1",
    img: "/images/agnes-lt-governor-letter.jpg",
    imgAlt: "Letter from Lieutenant Governor Salma Lakhani to Agnes Murphy on her 107th birthday",
    imgContain: true,
    size: "",
  },
];

export default function RoyalRecognition() {
  return (
    <section data-testid="royal-recognition-section" className="relative grain overflow-hidden bg-forest py-24 text-cream md:py-32">
      <div className="absolute inset-0 opacity-15">
        <img src={SCENES.waxLetters} alt="" className="h-full w-full object-cover" />
      </div>
      <div className="relative mx-auto max-w-5xl px-5 text-center">
        <Crown className="mx-auto h-10 w-10 text-gold" />
        <p className="mt-4 text-xs uppercase tracking-[0.3em] text-gold">Official Recognition</p>
        <h2 className="mt-3 font-serif text-4xl font-bold tracking-tight md:text-5xl">
          Honoured at the <span className="italic text-gold">Highest Level</span>
        </h2>
        <p className="mx-auto mt-5 max-w-2xl leading-relaxed text-cream/75">
          A life so extraordinary has been recognised by royalty, heads of government, and the city she calls home — not once, but again and again.
        </p>

        {/* Top row: wide certificates card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className={`mt-14 ${recognitions[0].rotate} transition-transform duration-500 hover:rotate-0`}
        >
          <div className="overflow-hidden rounded-sm border border-gold/40 bg-cream shadow-2xl">
            <img
              src={recognitions[0].img}
              alt={recognitions[0].imgAlt}
              className="h-72 w-full object-cover object-center"
            />
            <div className="p-7 text-left text-forest">
              <div className="gold-line mb-5 h-px w-16" />
              <p className="font-hand text-3xl text-terracotta">{recognitions[0].title}</p>
              <p className="mt-1 text-xs uppercase tracking-widest text-forest/50">{recognitions[0].year}</p>
              <p className="mt-4 font-serif italic leading-relaxed text-forest/80">{recognitions[0].note}</p>
            </div>
          </div>
        </motion.div>

        {/* Bottom two rows: 2-up grids */}
        {[[1, 2], [3, 4], [5]].map((pair, rowIdx) => (
          <div key={rowIdx} className="mt-10 grid gap-10 md:grid-cols-2">
            {pair.map((idx, i) => {
              const r = recognitions[idx];
              return (
                <motion.div
                  key={r.title}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.7, delay: i * 0.15 }}
                  className={`group ${r.rotate} transition-transform duration-500 hover:rotate-0`}
                >
                  <div className="overflow-hidden rounded-sm border border-gold/40 bg-cream shadow-2xl h-full">
                    <img
                      src={r.img}
                      alt={r.imgAlt}
                      className={`w-full ${r.imgContain ? "object-contain bg-[#f5f0e8]" : "h-72 object-cover object-top"}`}
                    />
                    <div className="p-7 text-left text-forest">
                      <div className="gold-line mb-5 h-px w-16" />
                      <p className="font-hand text-3xl text-terracotta">{r.title}</p>
                      <p className="mt-1 text-xs uppercase tracking-widest text-forest/50">{r.year}</p>
                      <p className="mt-4 font-serif italic leading-relaxed text-forest/80">{r.note}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ))}
      </div>
    </section>
  );
}
